import {EventMap, IEventManager, Listener} from '@fantasmagames/core/src/events/_interfaces/IEventManager';
import {IView} from '@fantasmagames/slot/src/common/_interfaces/IView';
import {inject} from 'inversify';
import {CoreDI} from '@fantasmagames/core/src/di/CoreDI';
import {Device} from '@fantasmagames/core/src/device/Device';
import DI from '../inversify/constants/DI';
import {UpdateManager} from '@fantasmagames/core/src/update/UpdateManager';
import {FullscreenManager, ScreenOrientation} from '@fantasmagames/core/src/screen/fullscreen/FullscreenManager';
import {ResourcesLoader} from '@fantasmagames/core/src/loader/ResourcesLoader';
import {ResolutionManager} from '@fantasmagames/core/src/screen/ResolutionManager';
import {SoundPlayer} from '@fantasmagames/slot/src/sound/SoundPlayer';
import {Translator} from '@fantasmagames/slot/src/modules/paytable/Translator';
import {TextStyles} from '@fantasmagames/core/src/display/texts/TextStyles';
import {Layout} from '@fantasmagames/slot/src/layout/Layout';
import {CurrencyFormatter} from '@fantasmagames/core/src/utils/CurrencyFormatter';
import {SpineUtils} from '@fantasmagames/core/src/spine/SpineUtils';
import {ScreenParams} from '@fantasmagames/core/src/screen/fullscreen/ScreenParams';
import {System} from '@fantasmagames/core/src/System';
import {HTMLUtils} from '@fantasmagames/core/src/html/HTMLUtils';
import {TimerUtils} from '@fantasmagames/core/src/update/TimerUtils';

export abstract class BasicView implements IEventManager, IView {
	protected _eventMap: EventMap = {};

	@inject(CoreDI.Device)
	private _device: Device;

	@inject(DI.PixiStageContainer)
	private _parent: PIXI.Container;

	@inject(CoreDI.UpdateManager)
	private _updateManager: UpdateManager;

	@inject(CoreDI.FullscreenManager)
	private _fullscreenManager: FullscreenManager;

	@inject(CoreDI.ResourceLoader)
	private _resources: ResourcesLoader;

	@inject(CoreDI.ResolutionManager)
	private _resolutionManager: ResolutionManager;

	@inject(DI.SoundPlayer)
	private _soundPlayer: SoundPlayer;

	@inject(DI.Translator)
	private _translator: Translator;

	@inject(DI.TextStyles)
	private _textStyles: TextStyles;

	@inject(DI.Layout)
	private _layout: Layout;

	@inject(DI.CurrencyFormatter)
	private _currencyFormatter: CurrencyFormatter;

	@inject(CoreDI.System)
	protected _system: System;

	private _overContainer: PIXI.Container;

	private _container: PIXI.Sprite;

	private _spineUtils: SpineUtils;

	private _timeUtils: TimerUtils;

	private _htmlUtils: HTMLUtils;

	public init(): void {
		this._container = new PIXI.Sprite();
		this._overContainer = new PIXI.Container();
		this._parent.addChild(this._overContainer);
		this._overContainer.addChild(this._container);

		this._spineUtils = new SpineUtils(this._resources, this._updateManager, this._container);
		this._timeUtils = new TimerUtils(this._updateManager);
	}

	public initHTMLUtils(): void {
		if (!this._htmlUtils) {
			this._htmlUtils = new HTMLUtils(this.resources, this.device, this.resolutionManager);
			this.resizeHtml = () => {
				this._htmlUtils.resizeItems();
			};
		}
	}

	public show(): void {
		this._container.visible = true;
	}

	public hide(): void {
		this._container.visible = false;
	}

	public hideContainer(): void {
		this._overContainer.visible = false;
	}

	public showContainer(): void {
		this._overContainer.visible = true;
	}

	public translate(): void {}

	public addToParent(): void {
		this._parent.addChild(this._container);
	}

	public removeFromParent(): void {
		this._parent.removeChild(this._container);
	}

	public isVisible(): boolean {
		return this._container.visible;
	}

	public on(id: string, callback: Function, owner: Object, priority: number = 0, data: any = null): void {
		if (!this._eventMap[id]) {
			this._eventMap[id] = [];
		}

		this._eventMap[id].push({callback, owner, priority, data});
		this._eventMap[id].sort((a, b) => {
			return b.priority - a.priority;
		});
	}

	public off(id: string, owner: Object): void {
		const listeners: Listener[] = this._eventMap[id];
		const index: number = listeners.findIndex((listener: Listener) => {
			return listener.owner === owner;
		});

		if (index > -1) {
			listeners.splice(index, 1);
		}
	}

	public emit(id: string, ...args: any[]): void {
		const listeners: Listener[] = this._eventMap[id];

		if (listeners) {
			for (const listener of listeners) {
				listener.callback(...args);
			}
		}
	}

	public emitDelayed(id: string, ...args: any[]): void {
		setTimeout(() => {
			this.emit(id, ...args);
		}, 0);
	}

	public hasListener(id: string, owner: Object): boolean {
		const listeners: Listener[] = this._eventMap[id];
		const listener: Listener = listeners.find((listener: Listener) => {
			return listener.owner === owner;
		});

		return !!listener;
	}

	protected forceUpdateOrientation(): void {
		if (this.device.isMobile) {
			if (this._fullscreenManager.orientation === ScreenOrientation.LANDSCAPE) {
				this.onLandscape();
			} else if (this._fullscreenManager.orientation === ScreenOrientation.PORTRAIT) {
				this.onPortrait();
			}
		}
	}

	public onFullscreenEntered(): void {}

	public onFullscreenExited(): void {}

	public onResized(screenParams: ScreenParams): void {
		this.resizeHtml();
	}

	public resizeHtml(): void {
		// Will be overridden when initializing htmlutils.
	}

	public get container(): PIXI.Sprite {
		return this._container;
	}

	public get updateManager(): UpdateManager {
		return this._updateManager;
	}

	public get resources(): ResourcesLoader {
		return this._resources;
	}

	public get device(): Device {
		return this._device;
	}

	public get resolutionManager(): ResolutionManager {
		return this._resolutionManager;
	}

	public get soundPlayer(): SoundPlayer {
		return this._soundPlayer;
	}

	public get fullscreenManager(): FullscreenManager {
		return this._fullscreenManager;
	}

	public get translator(): Translator {
		return this._translator;
	}

	public get textStyles(): TextStyles {
		return this._textStyles;
	}

	public get spineUtils(): SpineUtils {
		return this._spineUtils;
	}

	public get timeUtils(): TimerUtils {
		return this._timeUtils;
	}

	public get layout(): Layout {
		return this._layout;
	}

	public get currencyFormatter(): CurrencyFormatter {
		return this._currencyFormatter;
	}

	public get parent(): PIXI.Container {
		return this._parent;
	}

	public set htmlUtils(value: HTMLUtils) {
		this._htmlUtils = value;
	}

	public get htmlUtils(): HTMLUtils {
		return this._htmlUtils;
	}

	public get overContainer(): PIXI.Container {
		return this._overContainer;
	}

	public onLandscape(): void {}

	public onPortrait(): void {}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/common/view/BasicView.ts